import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { DutyHourType } from 'generated';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { InferType } from 'yup';

import getAddEditDutyCodeSchema from '../utils/addDutyCodeSchema';
import { getMaxDate } from 'utils/date';

import { mutations, queryKeys } from 'RestAPIs/rapini';

import { ControlledTimePeriods } from 'components/shared/ControlledFields';
import ValidFromValidTo from 'components/shared/ControlledFields/ControlledValidFromValidTo';
import DutyCodeForm from 'components/shared/DutyCodesGrid/DutyCodeBaseFields';
import { Dialog, LoadingOverlay, showSnackbar } from 'components/shared/index';

import Actions from '../../Actions';

type Props = {
  isOpen: boolean;
  handleClose: () => void;
  onSave: () => void;
  organizationalUnitId?: number;
};

export type DutyCodePeriodForm = {
  id: number;
  dutyHourTypeId: number;
  startTime: Date;
  endTime: Date;
  dutyHourType: DutyHourType;
};

const schema = getAddEditDutyCodeSchema();
export type DutyCodeForm = InferType<typeof schema>;

export const defaultFormValues: DutyCodeForm = {
  displayDutyCode: '',
  colorCode: '',
  validFrom: new Date(new Date().getFullYear(), 0, 1),
  validTo: getMaxDate(),
  periods: [
    {
      dutyCodePeriodId: undefined,
      startTime: dayjs('1970-01-01T07:00:00Z'),
      endTime: dayjs('1970-01-01T08:00:00Z'),
      dutyHourTypeId: -1
    }
  ],
  description: '',
  flags: {
    ROUTE: false,
    REQUEST_EXCLUDE: false
  }
};

const AddDutyCode = ({
  isOpen,
  handleClose,
  onSave,
  organizationalUnitId
}: Props) => {
  const { t } = useTranslation('common');
  const methods = useForm<DutyCodeForm>({
    defaultValues: defaultFormValues,
    resolver: yupResolver(getAddEditDutyCodeSchema())
  });

  const { control, handleSubmit, reset, watch } = methods;
  const { mutate: createDutyCode, isLoading: isCreating } =
    mutations.useCreateDutyCode();

  const QueryClient = useQueryClient();

  function handleReset() {
    reset(defaultFormValues);
  }

  function onSubmit(data: DutyCodeForm) {
    createDutyCode(
      {
        code: data.displayDutyCode,
        colorCode: data.colorCode,
        validFrom: data.validFrom.toISOString(),
        validTo: data.validTo.toISOString(),
        organizationalUnitId: organizationalUnitId,
        periods: data.periods.map((period) => ({
          dutyHourTypeId: period.dutyHourTypeId,
          startTime: period.startTime.toISOString(),
          endTime: period.endTime.toISOString()
        }))
      },
      {
        onSuccess: () => {
          QueryClient.invalidateQueries(queryKeys.getDutyCodes.name);
          onSave();
          handleReset();
        },
        onError: (error: any) => {
          const message = t(
            error?.response?.data?.error?.message || 'somethingWentWrong'
          );
          showSnackbar({
            severity: 'error',
            message: message
          });
        }
      }
    );
  }

  function onDialogClose() {
    handleClose();
    handleReset();
  }

  return (
    <Dialog
      open={isOpen}
      title={t('addDutyCode')}
      handleClose={onDialogClose}
      PaperProps={{
        sx: {
          minWidth: 550
        }
      }}
    >
      <FormProvider {...methods}>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <DutyCodeForm />
          <ValidFromValidTo control={control} />
          <ControlledTimePeriods
            control={control}
            watch={watch}
            hasDHTSelector
            hideBreakDuration
            dutyHourTypeDynFlags={
              organizationalUnitId ? ['AVAILABLE_DECENTRALIZED'] : undefined
            }
          />
          <Actions onDialogClose={onDialogClose} />
          <LoadingOverlay loading={isCreating} />
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default AddDutyCode;
